import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = [
    "modal",
    "urlInput",
    "submitButton",
    "signatureWarning",
    "modeSelector",
    "modeTab",
    "exactPanel",
    "modifyPanel",
    "bodyEditorContainer",
    "headersContainer",
    "queryContainer",
    "modifyWarnings",
    "formContainer",
    "headerRow",
    "queryRow",
    "identityRadio",
    "signatureRadio",
    "modifyIdentityRadio",
    "modifySignatureRadio",
  ];

  static values = {
    src: String,
  };

  connect() {
    this.activeModal = null;
    this.currentMode = "exact";
    this.bodyEdited = false;
    this.bodyValue = null;
    this.loaded = this.modalTargets.length > 0;
    this.loadingPromise = null;
  }

  async open(event) {
    event.preventDefault();
    const requestId = event.currentTarget.dataset.requestId;

    if (!this.loaded && this.hasSrcValue) {
      await this.loadFrame();
    }

    let modal = this.findModalForRequest(requestId);
    if (!modal && this.hasSrcValue) {
      await this.loadFrame({ force: true });
      modal = this.findModalForRequest(requestId);
    }
    if (!modal) {
      await this.nextTick();
      modal = this.findModalForRequest(requestId);
    }

    if (modal) {
      this.activeModal = modal;
      this.currentMode = modal.dataset.emailReplay === "true" ? "modify" : "exact";
      this.bodyEdited = false;
      this.bodyValue = null;
      modal.classList.remove("hidden");
      this.updateModeTabs(modal);
      this.updateWarnings(modal);
      const urlInput = modal.querySelector(
        "[data-replay-modal-target='urlInput']"
      );
      if (urlInput) {
        setTimeout(() => urlInput.focus(), 100);
      }
    }
  }

  async loadFrame(options = {}) {
    const force = options.force === true;
    const frame = this.element.querySelector("turbo-frame");
    if (!frame) return;
    const hasRenderedModal = frame.querySelector("[data-replay-modal-target='modal']") !== null;
    if (hasRenderedModal && !force) {
      this.loaded = true;
      return;
    }
    if (this.loaded && !force) return;
    if (this.loadingPromise && !force) return this.loadingPromise;

    if (force) {
      this.loaded = false;
    }

    this.loadingPromise = new Promise((resolve) => {
      const timeout = setTimeout(() => {
        frame.removeEventListener("turbo:frame-load", handler);
        this.loadingPromise = null;
        resolve();
      }, 5000);

      const handler = () => {
        clearTimeout(timeout);
        frame.removeEventListener("turbo:frame-load", handler);
        this.loaded = true;
        this.loadingPromise = null;
        resolve();
      };

      frame.addEventListener("turbo:frame-load", handler);
      frame.src = this.srcValue;
    });
    return this.loadingPromise;
  }

  nextTick() {
    return new Promise((resolve) => requestAnimationFrame(() => resolve()));
  }

  close(event) {
    if (event) event.preventDefault();
    const modal =
      event?.target?.closest("[data-replay-modal-target='modal']") ||
      this.activeModal;
    if (modal) {
      this.closeModal(modal);
      return;
    }
    this.modalTargets.forEach((m) => this.closeModal(m));
  }

  switchMode(event) {
    event.preventDefault();
    const mode = event.currentTarget.dataset.mode;
    if (mode === this.currentMode) return;
    this.currentMode = mode;
    const modal = this.modalForEvent(event);
    this.updateModeTabs(modal);
    this.updateWarnings(modal);
  }

  updateModeTabs(modal = this.activeModal) {
    if (!modal) return;
    const exactPanel = modal.querySelector(
      "[data-replay-modal-target='exactPanel']"
    );
    const modifyPanel = modal.querySelector(
      "[data-replay-modal-target='modifyPanel']"
    );
    const tabs = modal.querySelectorAll(
      "[data-replay-modal-target='modeTab']"
    );

    if (this.currentMode === "modify") {
      exactPanel?.classList.add("hidden");
      modifyPanel?.classList.remove("hidden");
    } else {
      exactPanel?.classList.remove("hidden");
      modifyPanel?.classList.add("hidden");
    }

    tabs.forEach((tab) => {
      if (tab.dataset.mode === this.currentMode) {
        tab.classList.add("bg-white", "text-gray-900", "shadow-sm");
        tab.classList.remove("text-gray-500", "hover:text-gray-700");
      } else {
        tab.classList.remove("bg-white", "text-gray-900", "shadow-sm");
        tab.classList.add("text-gray-500", "hover:text-gray-700");
      }
    });
  }

  optionChanged(event) {
    const modal = this.modalForEvent(event);
    this.updateWarnings(modal);
  }

  bodyChanged(event) {
    if (event.detail) {
      this.bodyEdited = event.detail.changed;
      this.bodyValue = event.detail.body;
    }
    const modal = this.modalForEvent(event);
    this.updateWarnings(modal);
  }

  addHeaderRow(event) {
    event.preventDefault();
    const modal = this.modalForEvent(event);
    const container = modal?.querySelector(
      "[data-replay-modal-target='headersContainer']"
    );
    if (!container) return;

    const row = document.createElement("div");
    row.className = "flex items-center gap-2";
    row.setAttribute("data-replay-modal-target", "headerRow");
    row.innerHTML = `
      <input type="text" placeholder="Header-Name" class="flex-1 px-2 py-1 rounded border-gray-300 text-xs font-mono" data-role="header-name">
      <input type="text" placeholder="value" class="flex-1 px-2 py-1 rounded border-gray-300 text-xs font-mono" data-role="header-value">
      <button type="button" class="text-gray-400 hover:text-red-500" data-action="click->replay-modal#removeHeaderRow">
        <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/></svg>
      </button>`;
    container.appendChild(row);
  }

  removeHeaderRow(event) {
    event.preventDefault();
    const row = event.target.closest("[data-replay-modal-target='headerRow']");
    if (row) row.remove();
    this.updateWarnings(this.modalForEvent(event));
  }

  addQueryRow(event) {
    event.preventDefault();
    const modal = this.modalForEvent(event);
    const container = modal?.querySelector(
      "[data-replay-modal-target='queryContainer']"
    );
    if (!container) return;

    const row = document.createElement("div");
    row.className = "flex items-center gap-2";
    row.setAttribute("data-replay-modal-target", "queryRow");
    row.innerHTML = `
      <input type="text" placeholder="param" class="flex-1 px-2 py-1 rounded border-gray-300 text-xs font-mono" data-role="query-name">
      <input type="text" placeholder="value" class="flex-1 px-2 py-1 rounded border-gray-300 text-xs font-mono" data-role="query-value">
      <button type="button" class="text-gray-400 hover:text-red-500" data-action="click->replay-modal#removeQueryRow">
        <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/></svg>
      </button>`;
    container.appendChild(row);
  }

  removeQueryRow(event) {
    event.preventDefault();
    const row = event.target.closest("[data-replay-modal-target='queryRow']");
    if (row) row.remove();
    this.updateWarnings(this.modalForEvent(event));
  }

  async submitReplay(event) {
    event.preventDefault();
    const modal = this.modalForEvent(event) || this.activeModal;
    if (!modal) return;

    const submitButton = modal.querySelector(
      "[data-replay-modal-target='submitButton']"
    );
    const urlInput = modal.querySelector(
      "[data-replay-modal-target='urlInput']"
    );

    if (!urlInput?.value) {
      this.showToast("Target URL is required", "error");
      return;
    }

    const isEmailReplay = modal.dataset.emailReplay === "true";
    if ((this.currentMode === "modify" || isEmailReplay) && this.bodyEdited && this.bodyValue != null) {
      try {
        const container = modal.querySelector(
          "[data-replay-modal-target='formContainer']"
        );
        if (container?.dataset.isJson === "true") {
          JSON.parse(this.bodyValue);
        }
      } catch (e) {
        this.showToast("Please fix the JSON body before replaying", "error");
        return;
      }
    }

    if (submitButton) {
      submitButton.disabled = true;
      submitButton.textContent = "Sending...";
    }

    try {
      const payload = this.buildPayload(modal);
      const formAction = modal.dataset.replayUrl;

      const response = await fetch(formAction, {
        method: "POST",
        body: JSON.stringify(payload),
        headers: {
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')
            .content,
          Accept: "application/json",
          "Content-Type": "application/json",
        },
      });

      const data = await response.json();

      if (data.success) {
        this.showToast(data.message || "Replay request sent!", "success");
        const isEmailReplay = modal.dataset.emailReplay === "true";
        this.closeModal(modal);
        if (isEmailReplay) {
          // Email replay is async; refresh to show new replay timeline entries once persisted.
          setTimeout(() => window.location.reload(), 300);
        }
      } else {
        this.showToast(data.message || "Failed to send replay request", "error");
      }
    } catch (error) {
      console.error("Replay request failed:", error);
      this.showToast("Network error: Failed to send replay request", "error");
    } finally {
      if (submitButton) {
        submitButton.disabled = false;
        submitButton.textContent = "Replay";
      }
    }
  }

  saveReplayCase(event) {
    event.preventDefault();
    const modal = this.modalForEvent(event) || this.activeModal;
    if (!modal) return;

    const prepareUrl = modal.dataset.replayCasePrepareUrl;
    if (!prepareUrl) {
      this.showToast("Sign in to save a Replay Case", "error");
      return;
    }

    const payload = this.buildPayload(modal);
    if (!payload.target_url) {
      this.showToast("Target URL is required", "error");
      return;
    }

    const { target_url: targetUrl, ...options } = payload;
    const form = document.createElement("form");
    form.method = "POST";
    form.action = prepareUrl;

    const fields = {
      authenticity_token: document.querySelector('meta[name="csrf-token"]')?.content || "",
      endpoint_id: modal.dataset.endpointId,
      source_id: modal.dataset.requestId,
      target_url: targetUrl,
      options_json: JSON.stringify(options),
    };
    Object.entries(fields).forEach(([name, value]) => {
      const input = document.createElement("input");
      input.type = "hidden";
      input.name = name;
      input.value = value || "";
      form.appendChild(input);
    });
    document.body.appendChild(form);
    form.submit();
  }

  // Keep old submit handler for form-based submission backward compat
  async submit(event) {
    event.preventDefault();
    const modal =
      event?.target?.closest("[data-replay-modal-target='modal']") ||
      this.activeModal;
    if (modal) {
      this.submitReplay({ preventDefault: () => {}, target: modal });
    }
  }

  buildPayload(modal) {
    const urlInput = modal.querySelector(
      "[data-replay-modal-target='urlInput']"
    );

    const payload = {
      target_url: urlInput?.value || "",
    };

    const isEmailReplay = modal.dataset.emailReplay === "true";

    if (this.currentMode === "exact" && !isEmailReplay) {
      const identityMode =
        modal.querySelector(
          'input[name="identity_mode"]:checked'
        )?.value || "original";
      const signatureMode =
        modal.querySelector(
          'input[name="signature_mode"]:checked'
        )?.value || "preserve";
      payload.identity_mode = identityMode;
      payload.signature_mode = signatureMode;
    } else {
      const container = modal.querySelector(
        "[data-replay-modal-target='formContainer']"
      );
      const originalHeaders = JSON.parse(
        container?.dataset.originalHeaders || "{}"
      );
      const originalQuery = JSON.parse(
        container?.dataset.originalQuery || "{}"
      );

      const identityMode =
        modal.querySelector(
          'input[name="modify_identity_mode"]:checked'
        )?.value || "original";
      const signatureMode =
        modal.querySelector(
          'input[name="modify_signature_mode"]:checked'
        )?.value || "strip";
      payload.identity_mode = identityMode;
      payload.signature_mode = signatureMode;

      // Body (from React island via custom event)
      if (this.bodyEdited && this.bodyValue != null) {
        payload.body_mode = "edited";
        payload.body_override = this.bodyValue;
      }

      if (isEmailReplay) {
        return payload;
      }

      // Headers
      const headerRows = modal.querySelectorAll(
        "[data-replay-modal-target='headerRow']"
      );
      const headerSet = {};
      const headerRemove = [];
      const currentHeaders = {};

      headerRows.forEach((row) => {
        const name = row.querySelector("[data-role='header-name']")?.value;
        const value = row.querySelector("[data-role='header-value']")?.value;
        if (name) currentHeaders[name] = value || "";
      });

      // Find removed headers
      for (const name of Object.keys(originalHeaders)) {
        if (!(name in currentHeaders)) {
          headerRemove.push(name);
        }
      }
      // Find set/changed headers
      for (const [name, value] of Object.entries(currentHeaders)) {
        if (!(name in originalHeaders) || originalHeaders[name] !== value) {
          headerSet[name] = value;
        }
      }

      if (
        Object.keys(headerSet).length > 0 ||
        headerRemove.length > 0
      ) {
        payload.headers = {};
        if (Object.keys(headerSet).length > 0) payload.headers.set = headerSet;
        if (headerRemove.length > 0) payload.headers.remove = headerRemove;
      }

      // Query params
      const queryRows = modal.querySelectorAll(
        "[data-replay-modal-target='queryRow']"
      );
      const querySet = {};
      const queryRemove = [];
      const currentQuery = {};

      queryRows.forEach((row) => {
        const name = row.querySelector("[data-role='query-name']")?.value;
        const value = row.querySelector("[data-role='query-value']")?.value;
        if (name) currentQuery[name] = value || "";
      });

      for (const name of Object.keys(originalQuery)) {
        if (!(name in currentQuery)) {
          queryRemove.push(name);
        }
      }
      for (const [name, value] of Object.entries(currentQuery)) {
        if (!(name in originalQuery) || originalQuery[name] !== value) {
          querySet[name] = value;
        }
      }

      if (
        Object.keys(querySet).length > 0 ||
        queryRemove.length > 0
      ) {
        payload.query = {};
        if (Object.keys(querySet).length > 0) payload.query.set = querySet;
        if (queryRemove.length > 0) payload.query.remove = queryRemove;
      }
    }

    return payload;
  }

  updateWarnings(modal = this.activeModal) {
    if (!modal) return;

    // Exact mode signature warning
    const signatureWarning = modal.querySelector(
      "[data-replay-modal-target='signatureWarning']"
    );
    if (signatureWarning) {
      const identityRadio = modal.querySelector(
        'input[name="identity_mode"][value="regenerate"]:checked'
      );
      const preserveRadio = modal.querySelector(
        'input[name="signature_mode"][value="preserve"]:checked'
      );
      const provider = modal.querySelector(
        "[data-replay-modal-target='formContainer']"
      )?.dataset.provider;
      if (identityRadio && preserveRadio && provider !== "shopify") {
        signatureWarning.classList.remove("hidden");
      } else {
        signatureWarning.classList.add("hidden");
      }
    }

    // Modify mode warnings (structured advice evaluation)
    const warningsEl = modal.querySelector(
      "[data-replay-modal-target='modifyWarnings']"
    );
    if (!warningsEl || this.currentMode !== "modify") {
      if (warningsEl) warningsEl.classList.add("hidden");
      return;
    }

    const container = modal.querySelector(
      "[data-replay-modal-target='formContainer']"
    );
    const bodyModified = this.bodyEdited;

    const signatureMode =
      modal.querySelector(
        'input[name="modify_signature_mode"]:checked'
      )?.value || "strip";
    const identityMode =
      modal.querySelector(
        'input[name="modify_identity_mode"]:checked'
      )?.value || "original";

    // Check if any mutation is active
    const originalHeaders = JSON.parse(
      container?.dataset.originalHeaders || "{}"
    );
    const originalQuery = JSON.parse(
      container?.dataset.originalQuery || "{}"
    );
    const headerRows = modal.querySelectorAll(
      "[data-replay-modal-target='headerRow']"
    );
    const queryRows = modal.querySelectorAll(
      "[data-replay-modal-target='queryRow']"
    );
    const filledHeaderRows = Array.from(headerRows).filter(
      (row) => row.querySelector("[data-role='header-name']")?.value?.trim()
    );
    const headersChanged =
      filledHeaderRows.length !== Object.keys(originalHeaders).length ||
      filledHeaderRows.some((row) => {
        const name = row.querySelector("[data-role='header-name']")?.value;
        const value = row.querySelector("[data-role='header-value']")?.value;
        return !(name in originalHeaders) || originalHeaders[name] !== value;
      });
    const filledQueryRows = Array.from(queryRows).filter(
      (row) => row.querySelector("[data-role='query-name']")?.value?.trim()
    );
    const queryChanged =
      filledQueryRows.length !== Object.keys(originalQuery).length ||
      filledQueryRows.some((row) => {
        const name = row.querySelector("[data-role='query-name']")?.value;
        const value = row.querySelector("[data-role='query-value']")?.value;
        return !(name in originalQuery) || originalQuery[name] !== value;
      });
    const anyMutation = bodyModified || headersChanged || queryChanged;

    // Build form state for condition evaluation
    const formState = {
      body_modified: bodyModified,
      any_mutation: anyMutation,
      identity_mode: identityMode,
      signature_mode: signatureMode,
    };

    // Evaluate structured advices from server
    let advices = [];
    try {
      advices = JSON.parse(container?.dataset.advices || "[]");
    } catch (e) {
      // ignore parse errors
    }

    const matchingWarnings = advices.filter((advice) => {
      const conditions = advice.conditions || {};
      return Object.entries(conditions).every(([key, expected]) => {
        return formState[key] === expected;
      });
    });

    if (matchingWarnings.length > 0) {
      warningsEl.textContent = "";
      matchingWarnings.forEach((a) => {
        const div = document.createElement("div");
        div.textContent = a.message;
        warningsEl.appendChild(div);
      });
      warningsEl.classList.remove("hidden");
    } else {
      warningsEl.classList.add("hidden");
    }
  }

  // Keep for backward compat
  identityModeChanged(event) {
    this.optionChanged(event);
  }

  findModalForRequest(requestId) {
    if (this.hasModalTarget) {
      if (this.modalTargets.length === 1) return this.modalTargets[0];
      if (!requestId) return null;
      return this.modalTargets.find((m) => m.dataset.requestId === requestId);
    }
    return null;
  }

  modalForEvent(event) {
    return (
      event?.target?.closest("[data-replay-modal-target='modal']") ||
      this.activeModal
    );
  }

  closeModal(modal) {
    if (!modal) return;
    if (modal.classList.contains("hidden")) return;
    modal.classList.add("hidden");
    if (this.activeModal === modal) {
      this.activeModal = null;
    }
  }

  showToast(message, type) {
    const toast = document.createElement("div");
    toast.className = `fixed top-4 right-4 max-w-sm p-4 rounded-lg shadow-lg z-[60] transition-all duration-300 transform translate-x-full ${
      type === "success"
        ? "bg-green-50 border border-green-200 text-green-800"
        : "bg-red-50 border border-red-200 text-red-800"
    }`;
    toast.textContent = message;
    document.body.appendChild(toast);

    setTimeout(() => toast.classList.remove("translate-x-full"), 100);
    setTimeout(() => {
      toast.classList.add("translate-x-full");
      setTimeout(() => {
        if (document.body.contains(toast)) document.body.removeChild(toast);
      }, 300);
    }, 5000);
  }
}
